/**
 * Form Validation Utilities
 * Provides validation functions for authentication forms
 */

import i18n from '../i18n';
import { FormErrors } from '../types/auth';

// Helper to get translated error message
const t = (key: string): string => i18n.t(key, { ns: 'auth' });

// ============================================
// VALIDATION RULES
// ============================================

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Username: 3-50 characters, only letters, numbers, underscores
const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,50}$/;

// Password: 8-16 chars, at least one uppercase, lowercase, number
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 16;
const HAS_UPPERCASE = /[A-Z]/;
const HAS_LOWERCASE = /[a-z]/;
const HAS_NUMBER = /[0-9]/;

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Validate email format
 */
export const validateEmail = (email: string): string | undefined => {
  if (!email) {
    return t('errors.emailRequired');
  }
  if (!EMAIL_REGEX.test(email)) {
    return t('errors.invalidEmail');
  }
  return undefined;
};

/**
 * Validate username format
 */
export const validateUsername = (username: string): string | undefined => {
  if (!username) {
    return t('errors.usernameRequired');
  }
  if (username.length < 3) {
    return t('errors.usernameTooShort');
  }
  if (username.length > 50) {
    return t('errors.usernameTooLong');
  }
  if (!USERNAME_REGEX.test(username)) {
    return t('errors.usernameInvalidChars');
  }
  return undefined;
};

/**
 * Validate password strength
 */
export const validatePassword = (password: string): string | undefined => {
  if (!password) {
    return t('errors.passwordRequired');
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    return t('errors.passwordTooShort');
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return t('errors.passwordTooLong');
  }
  if (!HAS_UPPERCASE.test(password)) {
    return t('errors.passwordNeedsUppercase');
  }
  if (!HAS_LOWERCASE.test(password)) {
    return t('errors.passwordNeedsLowercase');
  }
  if (!HAS_NUMBER.test(password)) {
    return t('errors.passwordNeedsNumber');
  }
  return undefined;
};

/**
 * Validate password confirmation
 */
export const validateConfirmPassword = (
  password: string,
  confirmPassword: string
): string | undefined => {
  if (!confirmPassword) {
    return t('errors.confirmPasswordRequired');
  }
  if (password !== confirmPassword) {
    return t('errors.passwordMismatch');
  }
  return undefined;
};

/**
 * Get password strength score (0-4)
 */
export const getPasswordStrength = (password: string): {
  score: number;
  label: string;
  color: string;
} => {
  let score = 0;

  if (password.length >= PASSWORD_MIN_LENGTH) score++;
  if (HAS_UPPERCASE.test(password) && HAS_LOWERCASE.test(password)) score++;
  if (HAS_NUMBER.test(password)) score++;
  // Bonus point for longer passwords
  if (password.length >= 12) score++;

  const labelKeys = [
    'passwordStrength.veryWeak',
    'passwordStrength.weak',
    'passwordStrength.fair',
    'passwordStrength.good',
    'passwordStrength.strong',
  ];
  const colors = [
    'bg-red-500',
    'bg-orange-500',
    'bg-yellow-500',
    'bg-blue-500',
    'bg-green-500',
  ];

  return {
    score,
    label: t(labelKeys[score]),
    color: colors[score],
  };
};

// ============================================
// FORM VALIDATION
// ============================================

interface RegisterFormData {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  birthDate?: string;
  countryCode?: string;
  isChildComputed?: boolean;
  parentEmail?: string;
  parentName?: string;
  parentConsentAgreed?: boolean;
}

/**
 * Validate registration form.
 *
 * When `isChildComputed` is true (client determines based on birthDate +
 * countryCode), the parent-contact fields become required. The server
 * re-validates the same logic as the source of truth.
 */
export const validateRegisterForm = (data: RegisterFormData): FormErrors => {
  const errors: FormErrors = {};

  const emailError = validateEmail(data.email);
  if (emailError) errors.email = emailError;

  const usernameError = validateUsername(data.username);
  if (usernameError) errors.username = usernameError;

  const passwordError = validatePassword(data.password);
  if (passwordError) errors.password = passwordError;

  const confirmError = validateConfirmPassword(data.password, data.confirmPassword);
  if (confirmError) errors.confirmPassword = confirmError;

  if (!data.birthDate) {
    errors.birthDate = 'Birth date is required';
  } else {
    const parsed = new Date(data.birthDate);
    if (Number.isNaN(parsed.getTime())) {
      errors.birthDate = 'Invalid birth date';
    } else if (parsed > new Date()) {
      errors.birthDate = 'Birth date cannot be in the future';
    }
  }

  if (!data.countryCode) {
    errors.countryCode = 'Country is required';
  }

  if (data.isChildComputed) {
    const parentEmailError = validateEmail(data.parentEmail || '');
    if (parentEmailError) errors.parentEmail = parentEmailError;
    if (!data.parentName || data.parentName.trim().length < 2) {
      errors.parentName = "Parent's name is required";
    }
    if (!data.parentConsentAgreed) {
      errors.parentConsentAgreed = 'You must agree on behalf of the child';
    }
  }

  return errors;
};

interface LoginFormData {
  email: string;
  password: string;
}

/**
 * Validate login form
 */
export const validateLoginForm = (data: LoginFormData): FormErrors => {
  const errors: FormErrors = {};

  const emailError = validateEmail(data.email);
  if (emailError) errors.email = emailError;

  if (!data.password) {
    errors.password = t('errors.passwordRequired');
  }

  return errors;
};

interface ForgotPasswordFormData {
  email: string;
}

/**
 * Validate forgot password form
 */
export const validateForgotPasswordForm = (data: ForgotPasswordFormData): FormErrors => {
  const errors: FormErrors = {};

  const emailError = validateEmail(data.email);
  if (emailError) errors.email = emailError;

  return errors;
};

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

/**
 * Validate reset password form
 */
export const validateResetPasswordForm = (data: ResetPasswordFormData): FormErrors => {
  const errors: FormErrors = {};

  const passwordError = validatePassword(data.password);
  if (passwordError) errors.password = passwordError;

  const confirmError = validateConfirmPassword(data.password, data.confirmPassword);
  if (confirmError) errors.confirmPassword = confirmError;

  return errors;
};

interface ChangePasswordFormData {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Validate change password form
 */
export const validateChangePasswordForm = (data: ChangePasswordFormData): FormErrors => {
  const errors: FormErrors = {};

  if (!data.oldPassword) {
    errors.oldPassword = t('errors.currentPasswordRequired');
  }

  const passwordError = validatePassword(data.newPassword);
  if (passwordError) errors.newPassword = passwordError;

  const confirmError = validateConfirmPassword(data.newPassword, data.confirmPassword);
  if (confirmError) errors.confirmPassword = confirmError;

  if (data.oldPassword && data.newPassword && data.oldPassword === data.newPassword) {
    errors.newPassword = t('errors.newPasswordSameAsOld');
  }

  return errors;
};

/**
 * Check if form has errors
 */
export const hasErrors = (errors: FormErrors): boolean => {
  return Object.keys(errors).length > 0;
};
